import {
  useQueryCreditPackSummary,
  useQueryMembershipSummary,
  useQueryProductSummary,
} from "@gymflow/api";
import {
  FormikInput,
  getEffectivePrice,
  onlyNumbersProps,
} from "@gymflow/common";
import {
  currencies,
  formatCurrency,
  LUXON_DATE_FORMAT,
} from "@gymflow/helpers";
import { UserMemberBean } from "@gymflow/types";
import { useFormikContext } from "formik";
import { DateTime } from "luxon";
import React, { useEffect, useState } from "react";

import { Can, Subject, Verb } from "../../permissions";
import { useClubSettings } from "../../providers";
import useGymflowModels from "../../store";
import { Button, ChangeCalculator, LabeledForm, Spinner } from "../atoms";
import DashedIcon from "../atoms/icons/DashedIcon";
import { WalletAndPromoCodeBlock } from "../atoms/WalletAndPromoCodeBlock";
import { DatePicker } from "../molecules/DatePicker/DatePicker";
import { ShopCheckoutPayment } from "./ShopCheckoutPayment";
import {
  ShopSidebarFormValues,
  TabsType,
} from "./ShopSidebar/ShopSidebarProvider";
import { ShopCartType } from "./ShopTypes";

export type ShopCheckoutProps = {
  cart: ShopCartType;
  selectedUser: UserMemberBean;
  promotionCode: string | undefined;
  setPromotionCode: React.Dispatch<React.SetStateAction<string | undefined>>;
  startDate: string;
  setStartDate: React.Dispatch<React.SetStateAction<string>>;
  allowChangingStartDate: boolean;
  setActiveTabId: (value: TabsType) => void;
  activeTabId: TabsType;
  customPaymentsEnabled: boolean;
  userHasActiveMembershipSubscription: boolean;
  currentWalletBalance: number;
};

// need to refactor it later to separate components + listen form logic (for whole component, not useState)
export const ShopCheckout: React.FC<ShopCheckoutProps> = ({
  cart,
  selectedUser,
  promotionCode,
  setPromotionCode,
  startDate,
  setStartDate,
  allowChangingStartDate,
  setActiveTabId,
  activeTabId,
  customPaymentsEnabled,
  userHasActiveMembershipSubscription,
  currentWalletBalance,
}) => {
  const { api } = useGymflowModels();
  const { club, featureFlags } = useClubSettings();

  const [promotionCodeInput, setPromotionCodeInput] = useState(
    promotionCode || "",
  );

  const formik = useFormikContext<ShopSidebarFormValues>();

  const { setFieldValue, values } = formik;

  const {
    data: membershipSummary,
    isFetching: membershipSummaryIsFetching,
    isSuccess: membershipSummaryIsSuccess,
  } = useQueryMembershipSummary({
    api,
    opts: {
      membershipId: cart.type === "MEMBERSHIP" ? cart.payload.id : 0,
      startDate,
      promotionCode: promotionCode,
      userMemberId: selectedUser.id,
    },
    enabled: cart.type === "MEMBERSHIP",
  });

  useEffect(() => {
    if (membershipSummaryIsSuccess) {
      setFieldValue(
        "priceOverride",
        membershipSummary.upfrontPriceDetails.totalAmountToPay,
      );
      setFieldValue("walletAmount", "");
    }
  }, [membershipSummaryIsSuccess, membershipSummary, setFieldValue]);

  const {
    data: creditPackSummary,
    isFetching: creditPackSummaryIsFetching,
    isSuccess: creditPackSummaryIsSuccess,
  } = useQueryCreditPackSummary({
    api,
    opts: {
      creditPackId: cart.type === "CREDIT_PACK" ? cart.payload.id : 0,
      promotionCode: promotionCode,
      userMemberId: selectedUser.id,
    },
    enabled: cart.type === "CREDIT_PACK",
  });

  useEffect(() => {
    if (creditPackSummaryIsSuccess) {
      setFieldValue(
        "priceOverride",
        creditPackSummary.upfrontPriceDetails.totalAmountToPay,
      );
      setFieldValue("walletAmount", "");
    }
  }, [creditPackSummary, creditPackSummaryIsSuccess, setFieldValue]);

  const {
    data: productSummary,
    isFetching: productSummaryIsFetching,
    isSuccess: productSummaryIsSuccess,
  } = useQueryProductSummary({
    api,
    opts: {
      products:
        cart.type === "PRODUCT"
          ? cart.payload
              .filter((e) => e.quantity > 0)
              .map((e) => ({
                productId: e.product.id,
                quantity: e.quantity,
              }))
          : [],
      promotionCode: promotionCode,
      userMemberId: selectedUser.id,
    },
    enabled: cart.type === "PRODUCT",
  });

  useEffect(() => {
    if (productSummaryIsSuccess) {
      setFieldValue(
        "priceOverride",
        productSummary.upfrontPriceDetails.totalAmountToPay,
      );
      setFieldValue("walletAmount", "");
    }
  }, [productSummaryIsSuccess, setFieldValue, productSummary]);

  const isSummaryFetching =
    membershipSummaryIsFetching ||
    creditPackSummaryIsFetching ||
    productSummaryIsFetching;

  const summary = membershipSummary || creditPackSummary || productSummary;

  // need for calculate price override when wallet amount changes
  useEffect(() => {
    if (!summary) {
      return;
    }

    const basePrice = summary.upfrontPriceDetails.totalAmountToPay;
    const walletAmount = Number(values.walletAmount) || 0;
    const newPriceOverride = basePrice - walletAmount;

    setFieldValue("priceOverride", parseFloat(newPriceOverride.toFixed(2)));
  }, [values.walletAmount, setFieldValue, summary]);

  return (
    <div className="flex flex-col gap-4">
      <ShopCheckoutPayment
        selectedUser={selectedUser}
        formik={formik}
        setActiveTabId={setActiveTabId}
        activeTabId={activeTabId}
        customPaymentsEnabled={customPaymentsEnabled}
      />
      <div className="-mx-8 flex w-[200%] border-b border-gray-200" />
      {cart.type === "MEMBERSHIP" && (
        <LabeledForm label="Membership Start Date">
          <DatePicker
            label={DateTime.fromFormat(startDate, LUXON_DATE_FORMAT)
              .setZone("local", { keepLocalTime: true })
              .toLocaleString(DateTime.DATE_MED)}
            mode="single"
            modifiers={{
              disabled: (d) => {
                if (allowChangingStartDate) {
                  return false;
                }
                const selected = DateTime.fromFormat(
                  startDate,
                  LUXON_DATE_FORMAT,
                ).setZone("local", { keepLocalTime: true });
                return !selected.hasSame(DateTime.fromJSDate(d), "day");
              },
            }}
            hidden={{
              before: DateTime.now().toJSDate(),
            }}
            selected={DateTime.fromFormat(startDate, LUXON_DATE_FORMAT)
              .setZone("local", { keepLocalTime: true })
              .toJSDate()}
            handleDateSave={
              allowChangingStartDate
                ? (newDate) => {
                    if (!newDate) {
                      return;
                    }
                    setStartDate(
                      DateTime.fromJSDate(newDate).toFormat(LUXON_DATE_FORMAT),
                    );
                  }
                : undefined
            }
          />
        </LabeledForm>
      )}
      {cart.type !== "GIFT_VOUCHER" &&
        (featureFlags.FE_USER_WALLETS ? (
          <WalletAndPromoCodeBlock
            promotionCodeInput={promotionCodeInput}
            setPromotionCode={setPromotionCode}
            setPromotionCodeInput={setPromotionCodeInput}
            promotionCode={promotionCode}
            summary={summary}
            isFetchingSummary={isSummaryFetching}
            walletBalance={currentWalletBalance}
            formik={formik}
          />
        ) : (
          <LabeledForm label="Promotional Code">
            <div className="bg-gray-0 inline-flex h-10 flex-row items-start justify-start rounded-lg border border-gray-300 shadow-sm">
              <div className="bg-gray-0 flex h-full flex-1 items-center justify-start gap-2 rounded-l-lg border-r border-gray-300">
                <input
                  placeholder="Enter Code"
                  className="size-full rounded-l-lg px-3 py-2 text-gray-500 outline-none"
                  value={promotionCodeInput}
                  onChange={(e) => {
                    setPromotionCodeInput(e.target.value);
                  }}
                  disabled={promotionCode !== undefined}
                />
              </div>
              <Button
                onClick={() => {
                  if (!promotionCode) {
                    setPromotionCode(promotionCodeInput);
                  } else {
                    setPromotionCode(undefined);
                    setPromotionCodeInput("");
                  }
                }}
                className="h-full min-h-0 !rounded-l-none !rounded-r-lg !border-none focus:!outline-none"
              >
                {isSummaryFetching ? (
                  <Spinner />
                ) : promotionCode === undefined ? (
                  "Apply"
                ) : (
                  "Remove"
                )}
              </Button>
            </div>
          </LabeledForm>
        ))}
      <div className="flex w-full flex-col gap-2">
        {!featureFlags.FE_USER_WALLETS &&
          summary &&
          promotionCode &&
          summary?.validPromotionCode !== "YES" && (
            <div className="flex w-full flex-row justify-between font-semibold text-red-500">
              The current promotion code is not valid
            </div>
          )}
        <DashedIcon className="my-3.5" />
        {cart.type === "GIFT_VOUCHER" && (
          <div className="flex w-full flex-row justify-between">
            <div>{cart.payload.name}</div>
            <div className="font-semibold">
              {formatCurrency(cart.payload.amount, club.defaultCurrency)}
            </div>
          </div>
        )}
        {summary && cart.type !== "PRODUCT" && (
          <div className="flex w-full flex-row justify-between">
            <div>{cart.payload.name}</div>
            <div className="font-semibold">
              {formatCurrency(
                summary.upfrontPriceDetails.totalAmountBeforeDiscount,
                club.defaultCurrency,
              )}
            </div>
          </div>
        )}
        {summary &&
          cart.type === "PRODUCT" &&
          cart.payload
            .filter((e) => e.quantity > 0)
            .map((e) => (
              <div
                key={e.product.id}
                className="flex w-full flex-row justify-between"
              >
                <div>{e.product.name}</div>
                <div className="flex flex-row gap-1">
                  <div>{e.quantity} x</div>
                  <div className="font-semibold">
                    {formatCurrency(
                      getEffectivePrice(
                        e.product,
                        userHasActiveMembershipSubscription,
                      ),
                      club.defaultCurrency,
                    )}
                  </div>
                </div>
              </div>
            ))}
        {!!summary &&
          summary.validPromotionCode === "YES" &&
          "promotionUpfrontAmount" in summary &&
          summary.promotionUpfrontAmount !== undefined && (
            <div className="text-success-500 flex w-full flex-row justify-between">
              <div className="font-semibold">Upfront Discount</div>
              <div className="font-semibold">
                {summary.promotionUpfrontDiscountType === "CURRENCY"
                  ? formatCurrency(
                      -summary.promotionUpfrontAmount,
                      club.defaultCurrency,
                    )
                  : `-${summary.promotionUpfrontAmount}%`}
              </div>
            </div>
          )}
        {!!summary &&
          summary.validPromotionCode === "YES" &&
          "promotionRecurringAmount" in summary &&
          summary.promotionRecurringAmount !== undefined && (
            <div className="text-success-500 flex w-full flex-row justify-between">
              <div className="font-semibold">
                Recurring Discount (each cycle)
              </div>
              <div className="font-semibold">
                {summary.promotionRecurringDiscountType === "CURRENCY"
                  ? formatCurrency(
                      -summary.promotionRecurringAmount,
                      club.defaultCurrency,
                    )
                  : `-${summary.promotionRecurringAmount}%`}
              </div>
            </div>
          )}
        {featureFlags.FE_USER_WALLETS && !!summary && !!values.walletAmount && (
          <div className="text-success-500 flex w-full flex-row justify-between">
            <div className="font-semibold">Wallet Balance</div>
            <div className="font-semibold">
              {formatCurrency(-values.walletAmount, club.defaultCurrency)}
            </div>
          </div>
        )}
        {summary && (
          <div className="flex w-full flex-row items-center justify-between">
            <Can I={Verb.Edit} a={Subject.CheckoutPrice}>
              <div className="font-bold">
                Amount Due ({currencies[club.defaultCurrency].symbol})
              </div>
              <div className="flex flex-row-reverse items-center gap-2">
                <FormikInput
                  className="w-24 text-right"
                  formikProps={formik}
                  name="priceOverride"
                  {...onlyNumbersProps}
                />
              </div>
            </Can>
            <Can I={Verb.Edit} not a={Subject.CheckoutPrice}>
              <div className="font-bold">Amount Due</div>
              {formatCurrency(
                summary.upfrontPriceDetails.totalAmountToPay -
                  (values.walletAmount || 0),
                club.defaultCurrency,
              )}
            </Can>
          </div>
        )}
      </div>
      {values?.inPersonPaymentMethod?.value.name === "Cash" && summary && (
        <ChangeCalculator price={values?.priceOverride || 0} />
      )}
    </div>
  );
};
